import { Box, Divider, Stack, Tooltip, Typography } from '@mui/material';
import React from 'react';

import { colors } from '../../../components/theme/theme';
import { useTranslation } from '../../../utils/translations';

interface DocumentMobileCardProps {
    row: Record<string, unknown>;
    canPreview: boolean;
    canDownload: boolean;
    onView: () => void;
    onDownload: () => void;
}

const FILE_TYPE_BG_COLORS: Record<string, string> = {
    pdf: colors.errorPreset[30],
    docx: colors.bluePreset[60],
    doc: colors.bluePreset[60],
    xlsx: colors.successPreset[20],
    xls: colors.successPreset[20],
    csv: colors.successPreset[20],
    txt: colors.greyPreset[20],
    xml: colors.warningPreset[80],
    jpeg: colors.bluePreset[60],
    jpg: colors.bluePreset[60],
};
const FILE_TYPE_COLORS: Record<string, string> = {
    pdf: colors.errorPreset[10],
    docx: colors.bluePreset[30],
    doc: colors.bluePreset[30],
    xlsx: colors.successPreset[50],
    xls: colors.successPreset[50],
    csv: colors.successPreset[50],
    txt: colors.greyPreset[80],
    xml: colors.warningPreset[50],
    jpeg: colors.bluePreset[30],
    jpg: colors.bluePreset[30],
};

const getFileExtension = (filename: string): string => filename.split('.').pop()?.toLowerCase() || '';

/**
 * Mobile/tablet-only card rendering of a single documents-list row for the documents screen
 * (pages/documents/documents.tsx). Desktop keeps using CommonTable.
 */
export const DocumentMobileCard: React.FC<DocumentMobileCardProps> = ({
    row,
    canPreview,
    canDownload,
    onView,
    onDownload,
}) => {
    const { t } = useTranslation();
    const originalDocument = row.originalDocument as { name?: string; fileId?: string } | undefined;
    const name = originalDocument?.name || '';
    const fileId = originalDocument?.fileId || '';
    const ext = getFileExtension(name).toUpperCase();
    const bgColor = FILE_TYPE_BG_COLORS[ext.toLowerCase()] || colors.greyPreset[80];
    const fgColor = FILE_TYPE_COLORS[ext.toLowerCase()] || colors.greyPreset[100];
    const date = String(row.date || '');
    const size = String(row.size || '');
    const fileType = String(row.fileType || '—');

    return (
        <Box
            sx={{
                border: `1px solid ${colors.greyPreset[30]}`,
                borderRadius: 1.5,
                backgroundColor: colors.fullWhite,
                overflow: 'hidden',
                flexShrink: 0,
                p: 2,
            }}
        >
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" gap={1} flexWrap="nowrap">
                <Stack gap={0.5} sx={{ minWidth: 0, flex: 1 }}>
                    <Stack direction="row" gap={1} alignItems="center" sx={{ minWidth: 0 }}>
                        <Box
                            sx={{
                                flexShrink: 0,
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                height: 24,
                                px: 1,
                                borderRadius: '6px',
                                bgcolor: bgColor,
                                color: fgColor,
                                fontSize: 13,
                                fontWeight: 700,
                                lineHeight: 1,
                                whiteSpace: 'nowrap',
                                textTransform: 'uppercase',
                            }}
                        >
                            {ext || 'FILE'}
                        </Box>
                        <Tooltip title={name} arrow placement="bottom">
                            <Typography variant="subtitle2" fontWeight={700} noWrap sx={{ minWidth: 0 }}>
                                {name}
                            </Typography>
                        </Tooltip>
                    </Stack>
                    {fileId && (
                        <Typography variant="body2" color="textSecondary" noWrap sx={{ minWidth: 0 }}>
                            {fileId}
                        </Typography>
                    )}
                </Stack>
                <Stack direction="row" gap={2} alignItems="center" flexShrink={0}>
                    {canPreview && (
                        <Box
                            role="button"
                            tabIndex={0}
                            onClick={onView}
                            onKeyDown={event => {
                                if (event.key === 'Enter' || event.key === ' ') {
                                    event.preventDefault();
                                    onView();
                                }
                            }}
                            sx={{ display: 'flex', cursor: 'pointer' }}
                            aria-label={t('common.Preview') || 'View'}
                        >
                            <span className="icon-eye" style={{ fontSize: 18, color: colors.greyPreset[100] }} />
                        </Box>
                    )}
                    {canDownload && (
                        <Box
                            role="button"
                            tabIndex={0}
                            onClick={onDownload}
                            onKeyDown={event => {
                                if (event.key === 'Enter' || event.key === ' ') {
                                    event.preventDefault();
                                    onDownload();
                                }
                            }}
                            sx={{ display: 'flex', cursor: 'pointer' }}
                            aria-label={t('common.Download') || 'Download'}
                        >
                            <span className="icon-download" style={{ fontSize: 18, color: colors.greyPreset[100] }} />
                        </Box>
                    )}
                </Stack>
            </Stack>

            <Box
                sx={{
                    mt: 1.5,
                    p: 1.5,
                    borderRadius: 1,
                    backgroundColor: colors.greyPreset[90],
                }}
            >
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', columnGap: 2, rowGap: 0.25 }}>
                    <Stack gap={0.25} sx={{ minWidth: 0 }}>
                        <Typography
                            variant="caption"
                            color={colors.greyPreset[100]}
                            fontWeight={700}
                            noWrap
                            sx={{ textTransform: 'uppercase' }}
                        >
                            {t('Documents.table.date') || 'Created Date'}
                        </Typography>
                        <Typography variant="body2" fontWeight={600} noWrap>
                            {date || '—'}
                        </Typography>
                    </Stack>
                    <Stack gap={0.25} sx={{ minWidth: 0, alignItems: 'flex-end' }}>
                        <Typography
                            variant="caption"
                            color={colors.greyPreset[100]}
                            fontWeight={700}
                            noWrap
                            sx={{ textTransform: 'uppercase' }}
                        >
                            {t('Documents.table.size') || 'File Size'}
                        </Typography>
                        <Typography variant="body2" fontWeight={600} noWrap>
                            {size || '—'}
                        </Typography>
                    </Stack>
                </Box>

                <Divider sx={{ my: 1.5 }} />

                <Stack gap={0.25} sx={{ minWidth: 0 }}>
                    <Typography
                        variant="caption"
                        color={colors.greyPreset[100]}
                        fontWeight={700}
                        noWrap
                        sx={{ textTransform: 'uppercase' }}
                    >
                        {t('Documents.table.fileType') || 'Category'}
                    </Typography>
                    <Typography variant="body2" fontWeight={600} noWrap>
                        {fileType}
                    </Typography>
                </Stack>
            </Box>
        </Box>
    );
};
